// React
import { useTranslation } from '@refinedev/core';
import { useDocumentTitle } from '@refinedev/react-router-v6';

// Mantine
import {
  Paper,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';

// Auth
import { useCurrentUserStore } from 'store/UserStore';
import { useCurrentSiteStore } from 'store/SiteStore';

// Custom Components
import { DashboardBanner } from 'components/common/DashboardBanner';
import { ProviderMetrics } from './components/ProviderMetrics';
import { ProviderDashboardMaintenance } from './components/ProviderDashboardMaintenance';

// Hooks
import { useIsMobile, useProviderAllSites } from 'hooks';
import { useRemoteFeatureFlag } from 'hooks/useRemoteFeatureFlag';
import type { Provider } from 'interfaces';
import { toTitleCase } from 'utils/stringUtils';

interface ProviderDashboardProps {
  provider?: Provider;
  userID?: string;
}

export const ProviderDashboard = ({
  provider,
  userID,
}: ProviderDashboardProps) => {
  useDocumentTitle('Provider Dashboard | QBilling');
  const { translate } = useTranslation();
  const isMaintenance = useRemoteFeatureFlag('provider_dashboard_maintenance');
  const { lastName, firstName, providers } = useCurrentUserStore();
  const title = provider?.title ?? providers?.[0]?.title;
  const isMobile = useIsMobile(700);
  const { isAllSites } = useProviderAllSites();
  const { name: siteName } = useCurrentSiteStore();
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('dark');
  const dashboardTitle =
    !isAllSites && siteName
      ? translate('pages:providerDashboard.siteTitle', { siteName })
      : translate('pages:providerDashboard.title');
  const providerName = `${title || 'Dr.'} ${toTitleCase(
    provider?.firstName ?? firstName,
    provider?.lastName ?? lastName
  )}`;

  return (
    <Paper
      p="md"
      radius={'md'}
      bg="transparent"
      data-testid="provider-dashboard-page"
      mih={isMaintenance ? 'calc(100dvh - 3.75rem)' : undefined}
      style={
        isMaintenance ? { display: 'flex', flexDirection: 'column' } : undefined
      }
    >
      {!isMaintenance && (
        <DashboardBanner configKey="provider_dashboard_banner" />
      )}
      <Stack gap={4} mb="md" px={isMobile ? 4 : 8} align="flex-start">
        <Stack gap={4}>
          <Title
            order={isMobile ? 2 : 1}
            fw={700}
            data-testid="provider-dashboard-title"
          >
            {dashboardTitle}
          </Title>
        </Stack>
        <Text
          size={isMobile ? 'sm' : 'md'}
          fw={600}
          c={theme.colors[`primary-${computedColorScheme}`][0]}
        >
          {providerName}
        </Text>
      </Stack>
      {isMaintenance ? (
        <ProviderDashboardMaintenance />
      ) : (
        <ProviderMetrics userID={userID} providerTitle={title} />
      )}
    </Paper>
  );
};
